import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './FotosEvento.css';

const FotosEvento = () => {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [fotos, setFotos] = useState([]);
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewModal, setPreviewModal] = useState({ isOpen: false, fotoId: null, imageUrl: null });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [editModal, setEditModal] = useState({ isOpen: false, fotoId: null, numeroCorredor: '' });
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [priceModal, setPriceModal] = useState({ isOpen: false, fotoId: null, precio: '' });
  const [loadingPrice, setLoadingPrice] = useState(false);

  useEffect(() => {
    loadDatos();
  }, [eventoId]);

  const loadDatos = async () => {
    try {
      setLoading(true);
      // Cargar datos del evento
      const eventoResponse = await api.get(`/eventos/${eventoId}`);
      setEvento(eventoResponse.data);

      // Cargar fotos del evento
      const fotosResponse = await api.get(`/fotos/evento/${eventoId}`);
      setFotos(fotosResponse.data);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar las fotos del evento');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFoto = async (fotoId) => {
    if (window.confirm('¿Estás seguro de eliminar esta foto?')) {
      try {
        await api.delete(`/fotos/${fotoId}`);
        setFotos(fotos.filter(f => f.id !== fotoId));
      } catch (err) {
        console.error('Error al eliminar foto:', err);
        alert('Error al eliminar la foto');
      }
    }
  };

  const handleShowPreview = async (fotoId) => {
    setLoadingPreview(true);
    setPreviewModal({ isOpen: true, fotoId, imageUrl: null });
    
    try {
      // Llamar al endpoint de preview que devuelve la imagen comprimida con marca de agua
      const response = await api.get(`/fotos/preview/${fotoId}`, {
        responseType: 'blob' // Importante para recibir imagen
      });
      
      // Crear URL temporal para mostrar la imagen
      const imageUrl = URL.createObjectURL(response.data);
      setPreviewModal({ isOpen: true, fotoId, imageUrl });
    } catch (err) {
      console.error('Error al cargar preview:', err);
      alert('Error al cargar la vista previa');
      setPreviewModal({ isOpen: false, fotoId: null, imageUrl: null });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    // Liberar la URL del objeto para evitar fugas de memoria
    if (previewModal.imageUrl) {
      URL.revokeObjectURL(previewModal.imageUrl);
    }
    setPreviewModal({ isOpen: false, fotoId: null, imageUrl: null });
  };

  const handleOpenEditModal = (foto) => {
    setEditModal({ 
      isOpen: true, 
      fotoId: foto.id, 
      nombreArchivo: foto.nombreArchivo,
      numeroActual: foto.numeroCorredorDetectado || null,
      numeroCorredor: '' 
    });
  };

  const handleCloseEditModal = () => {
    setEditModal({ isOpen: false, fotoId: null, nombreArchivo: '', numeroCorredor: '' });
  };

  const handleUpdateNumeroCorredor = async () => {
    if (!editModal.numeroCorredor.trim()) {
      alert('Por favor ingresa un número de corredor');
      return;
    }

    setLoadingEdit(true);
    try {
      const response = await api.put(`/fotos/${editModal.fotoId}/numero-corredor`, {
        numeroCorredor: editModal.numeroCorredor.trim()
      });

      // Actualizar la foto en la lista
      setFotos(fotos.map(f => 
        f.id === editModal.fotoId 
          ? { ...f, numeroCorredorDetectado: editModal.numeroCorredor.trim(), estado: 'DISPONIBLE' }
          : f
      ));

      alert('Número de corredor actualizado correctamente');
      handleCloseEditModal();
    } catch (err) {
      console.error('Error al actualizar número de corredor:', err);
      alert(err.response?.data || 'Error al actualizar el número de corredor');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleOpenPriceModal = (foto) => {
    setPriceModal({ 
      isOpen: true, 
      fotoId: foto.id, 
      nombreArchivo: foto.nombreArchivo,
      precio: foto.precio || evento?.precioFoto || '30'
    });
  };

  const handleClosePriceModal = () => {
    setPriceModal({ isOpen: false, fotoId: null, nombreArchivo: '', precio: '' });
  };

  const handleUpdatePrecio = async () => {
    const precio = parseFloat(priceModal.precio);
    
    if (isNaN(precio) || precio <= 0) {
      alert('Por favor ingresa un precio válido mayor a 0');
      return;
    }

    setLoadingPrice(true);
    try {
      await api.put(`/fotos/${priceModal.fotoId}/precio`, {
        precio: precio
      });

      // Actualizar la foto en la lista
      setFotos(fotos.map(f => 
        f.id === priceModal.fotoId 
          ? { ...f, precio: precio }
          : f
      ));

      alert(`Precio actualizado a $${precio.toFixed(2)} MXN`);
      handleClosePriceModal();
    } catch (err) {
      console.error('Error al actualizar precio:', err);
      alert(err.response?.data || 'Error al actualizar el precio');
    } finally {
      setLoadingPrice(false);
    }
  };

  if (loading) {
    return <LoadingSpinner mensaje="Cargando fotos..." />;
  }

  return (
    <div className="fotos-evento-container">
      <div className="header">
        <button onClick={() => navigate('/eventos')} className="button-secondary">
          ← Volver a Eventos
        </button>
        {evento && (
          <div className="evento-info">
            <h2>{evento.nombre}</h2>
            <p>{evento.descripcion}</p>
            <div className="evento-actions-header">
              <p className="evento-stats">
                Total de fotos: {fotos.length}
              </p>
              <button 
                onClick={() => navigate(`/upload/${eventoId}`)} 
                className="button"
              >
                + Agregar Fotos
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {fotos.length === 0 ? (
        <div className="empty-state">
          <p>No hay fotos subidas para este evento</p>
          <button onClick={() => navigate(`/upload/${eventoId}`)} className="button">
            Subir Fotos
          </button>
        </div>
      ) : (
        <div className="fotos-list">
          {fotos.map((foto) => (
            <div key={foto.id} className="foto-item">
              <div className="foto-placeholder-small">
                <span className="foto-icon-small">✓</span>
              </div>
              <div className="foto-details-inline">
                <div className="foto-row">
                  <p><strong>ID:</strong> {foto.id}</p>
                  <p><strong>Archivo:</strong> {foto.nombreArchivo}</p>
                  {foto.numeroCorredorDetectado && (
                    <p>
                      <strong>Corredor:</strong> 
                      <span className="corredor-badge">#{foto.numeroCorredorDetectado}</span>
                    </p>
                  )}
                  <p>
                    <strong>Precio:</strong> 
                    <span className="precio-badge">${(foto.precio || evento?.precioFoto || 30).toFixed(2)} MXN</span>
                  </p>
                </div>
                <div className="foto-row">
                  <p>
                    <strong>Estado:</strong> 
                    <span className={`estado-badge estado-${foto.estado.toLowerCase()}`}>
                      {foto.estado === 'PROCESANDO' 
                        ? 'No se detectó número - Favor de dar click en Editar Número' 
                        : foto.estado}
                    </span>
                  </p>
                  {foto.vendida && (
                    <p><span className="vendida-badge">VENDIDA</span></p>
                  )}
                  <p><strong>Subida:</strong> {new Date(foto.fechaSubida).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="foto-actions">
                <button 
                  onClick={() => handleOpenEditModal(foto)} 
                  className="button-edit"
                  title="Editar número de corredor"
                  disabled={foto.vendida}
                >
                  ✏️ Editar Número
                </button>
                <button 
                  onClick={() => handleOpenPriceModal(foto)} 
                  className="button-edit"
                  title="Editar precio"
                  disabled={foto.vendida}
                >
                  💲 Editar Precio
                </button>
                <button 
                  onClick={() => handleShowPreview(foto.id)} 
                  className="button-preview"
                  title="Ver vista previa"
                >
                  👁️ Ver Preview
                </button>
                <button 
                  onClick={() => handleDeleteFoto(foto.id)} 
                  className="button-delete"
                  title="Eliminar foto"
                  disabled={foto.vendida}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Preview */}
      {previewModal.isOpen && (
        <div className="modal-overlay" onClick={handleClosePreview}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleClosePreview}>×</button>
            <h3>Vista Previa - Foto #{previewModal.fotoId}</h3>
            {loadingPreview ? (
              <div className="preview-loading">
                <p>Cargando vista previa...</p>
                <p className="cost-info">💡 Descargando y comprimiendo imagen (sin costo adicional)</p>
              </div>
            ) : previewModal.imageUrl ? (
              <div className="preview-image-container">
                <img 
                  src={previewModal.imageUrl} 
                  alt="Vista previa" 
                  className="preview-image"
                />
                <p className="preview-info">
                  ℹ️ Esta es una versión comprimida (~50KB) con marca de agua. 
                  La imagen original está segura en tu almacenamiento.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Modal de Edición de Número de Corredor */}
      {editModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content modal-edit" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseEditModal}>×</button>
            <h3>Editar Número de Corredor</h3>
            <div className="edit-form">
              <p className="edit-info">
                <strong>Foto:</strong> {editModal.nombreArchivo}<br/>
                <small>Puedes corregir el número aunque el OCR haya detectado uno. Número actual: <strong>{editModal.numeroActual ? `#${editModal.numeroActual}` : 'no detectado'}</strong>.</small>
              </p>
              <div className="form-group">
                <label htmlFor="numeroCorredor">Número de Corredor:</label>
                <input
                  type="text"
                  id="numeroCorredor"
                  value={editModal.numeroCorredor}
                  onChange={(e) => setEditModal({ ...editModal, numeroCorredor: e.target.value })}
                  placeholder="Ej: 154"
                  className="form-input"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleUpdateNumeroCorredor();
                  }}
                />
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleUpdateNumeroCorredor} 
                  className="button"
                  disabled={loadingEdit}
                >
                  {loadingEdit ? 'Guardando...' : 'Guardar'}
                </button>
                <button 
                  onClick={handleCloseEditModal} 
                  className="button-secondary"
                  disabled={loadingEdit}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Precio */}
      {priceModal.isOpen && (
        <div className="modal-overlay" onClick={handleClosePriceModal}>
          <div className="modal-content modal-edit" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleClosePriceModal}>×</button>
            <h3>Editar Precio de Foto</h3>
            <div className="edit-form">
              <p className="edit-info">
                <strong>Foto:</strong> {priceModal.nombreArchivo}<br/>
                <small>Modifica el precio individual de esta foto. El precio predeterminado del evento es ${evento?.precioFoto || 30} MXN.</small>
              </p>
              <div className="form-group">
                <label htmlFor="precio">Precio (MXN):</label>
                <input
                  type="number"
                  id="precio"
                  value={priceModal.precio}
                  onChange={(e) => setPriceModal({ ...priceModal, precio: e.target.value })}
                  placeholder="Ej: 30.00"
                  className="form-input"
                  step="0.01"
                  min="0.01"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleUpdatePrecio();
                  }}
                />
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleUpdatePrecio} 
                  className="button"
                  disabled={loadingPrice}
                >
                  {loadingPrice ? 'Guardando...' : 'Guardar'}
                </button>
                <button 
                  onClick={handleClosePriceModal} 
                  className="button-secondary"
                  disabled={loadingPrice}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FotosEvento;
